// src/components/CheckoutPixAsaas.js
"use client"

import { useEffect, useState } from "react"
import axios from "axios"
import { motion } from "framer-motion"
import { Copy, CheckCircle, AlertCircle, Clock } from "lucide-react"
import "../styles/checkout.css"

const API = process.env.REACT_APP_API_URL || "https://api.ticketdovaqueiro.com/api"

export default function CheckoutPixAsaas({
  pedidoId,
  total,            // total com taxa
  nomeVaqueiro,
  email,
  cpfCnpj,
  phone,
  eventoid,
  nomeEvento,
  onSuccess,
}) {
  const [qrCode, setQrCode] = useState(null)
  const [pixCopiaCola, setPixCopiaCola] = useState("")
  const [erro, setErro] = useState("")
  const [expiraEm, setExpiraEm] = useState(null)
  const [contador, setContador] = useState("")
  const [copiado, setCopiado] = useState(false)
  const [expirado, setExpirado] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (typeof window === "undefined") return
    const metodoAtual = "pix"
    const metodoSelecionado = sessionStorage.getItem("metodoSelecionado")
    if (metodoSelecionado !== metodoAtual) {
      // PIX ainda não foi selecionado — não gera QR e para o spinner
      setLoading(false)
      return
    }

    const gerarPix = async () => {
      setLoading(true)
      try {
        setErro("")

        let usuarioLogado = null
        try {
          usuarioLogado = JSON.parse(localStorage.getItem("user"))
        } catch {}

        const resp = await axios.post(`${API}/pagamento/asaas/pix`, {
          pedidoId,
          totalPedido: Number(total),
          nomeVaqueiro,
          email: email || usuarioLogado?.email || "",
          cpfCnpj: cpfCnpj || usuarioLogado?.cpf || "",
          phone: phone || usuarioLogado?.whatsapp || "",
          descricao: `Evento ${nomeEvento} (${eventoid})`,
        })

        if (resp?.data) {
  setQrCode(resp.data.qrCodeBase64 || null)
  setPixCopiaCola(resp.data.copiaECola || "")
  const exp = resp.data.expiracao ? new Date(resp.data.expiracao) : null
  setExpiraEm(exp && !isNaN(exp.getTime()) ? exp : null)
  if (typeof onSuccess === "function") onSuccess(resp.data)



} else {
  throw new Error("Resposta inválida da API")
}

      } catch (e) {
        console.error("Erro ao gerar Pix Asaas:", e?.response?.data || e.message)
        const detail = e?.response?.data?.detail || e.message || "Falha ao gerar Pix Asaas."
        setErro(detail)
      } finally {
        setLoading(false)
      }
    }

    gerarPix()
  }, [API, pedidoId, total, nomeVaqueiro, email, cpfCnpj, phone, eventoid, nomeEvento, onSuccess])

  useEffect(() => {
    if (!expiraEm) return
    const interval = setInterval(() => {
      const agora = new Date()
      const diferenca = expiraEm - agora
      if (diferenca <= 0) {
        setContador("Expirado")
        setExpirado(true)
        clearInterval(interval)
      } else {
        const minutos = Math.floor(diferenca / 60000)
        const segundos = Math.floor((diferenca % 60000) / 1000)
        setContador(`${String(minutos).padStart(2, "0")}:${String(segundos).padStart(2, "0")}`)
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [expiraEm])

  const copiarCodigo = () => {
    if (!pixCopiaCola) return
    navigator.clipboard.writeText(pixCopiaCola)
    setCopiado(true)
    setTimeout(() => setCopiado(false), 2000)
  }

  if (loading) {
    return (
      <div className="pix-loading">
        <div className="loading-spinner"></div>
        <p>Gerando QR Code...</p>
      </div>
    )
  }

  if (erro) {
    return (
      <div className="pix-erro">
        <AlertCircle size={24} />
        <p>{erro}</p>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="pix-container"
    >
      <h3>Pagamento via Pix</h3>

      {qrCode && (
        <>
          <p>Escaneie o QR Code abaixo ou copie o código:</p>

          <motion.img
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            src={`data:image/png;base64,${qrCode}`}
            alt="QR Code Pix"
            className="qr-code-img"
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="copia-cola-container"
          >
            <input type="text" value={pixCopiaCola} readOnly className="copia-cola-input" />
          </motion.div>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={copiarCodigo}
            className="btn-copiar"
            disabled={expirado}
          >
            {copiado ? <CheckCircle size={18} /> : <Copy size={18} />}
            {copiado ? "Código copiado!" : "Copiar Código"}
          </motion.button>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="expiracao-timer"
          >
            <Clock size={16} className="timer-icon" />
            Expira em: {contador || "Carregando..."}
          </motion.p>

          {expirado && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="pix-expirado">
              <AlertCircle size={18} />O código Pix expirou. Por favor, gere um novo código para realizar o pagamento.
            </motion.div>
          )}
        </>
      )}
    </motion.div>
  )
}